import * as THREE from "three";

export const LOOM_INTERACTION_HEAVY_OVERLAY = "loomInteractionHeavyOverlay" as const;

export interface LoomViewerInteractionQualityController {
  begin: () => void;
  dispose: () => void;
  end: () => void;
  isReduced: () => boolean;
  keepMoving: () => void;
  refreshOverlays: () => void;
  settle: () => void;
}

export interface LoomViewerCameraSettlementTracker {
  changed: () => void;
  frame: (cameraStillMoving: boolean) => void;
}

export function createLoomViewerCameraSettlementTracker(
  publish: () => void,
): LoomViewerCameraSettlementTracker {
  let pending = false;
  return {
    changed: () => { pending = true; },
    frame: (cameraStillMoving) => {
      if (cameraStillMoving) { pending = true; return; }
      if (!pending) return;
      pending = false; publish();
    },
  };
}

export function recordLoomViewerFrameEvidence(
  host: HTMLElement,
  renderer: THREE.WebGLRenderer,
  startedAt: number,
): void {
  Object.assign(host.dataset, {
    canvasPixelCount: String(renderer.domElement.width * renderer.domElement.height),
    renderCalls: String(renderer.info.render.calls),
    renderFrameMs: (performance.now() - startedAt).toFixed(3),
    renderLines: String(renderer.info.render.lines),
    renderTriangles: String(renderer.info.render.triangles),
  });
}

interface LoomViewerInteractionQualityOptions {
  fullPixelRatio: () => number;
  host: HTMLElement;
  reducedPixelRatio?: number;
  renderer: Pick<THREE.WebGLRenderer, "setPixelRatio">;
  requestRender: () => void;
  root: THREE.Object3D;
  settleDelayMs?: number;
}

/**
 * Keeps camera motion responsive without changing geometry authority. Dense
 * inspection overlays and Retina supersampling return after the camera rests.
 */
export function createLoomViewerInteractionQualityController({
  fullPixelRatio,
  host,
  reducedPixelRatio = 1,
  renderer,
  requestRender,
  root,
  settleDelayMs = 140,
}: LoomViewerInteractionQualityOptions): LoomViewerInteractionQualityController {
  let reduced = false;
  let interactionActive = false;
  let restoreTimer: ReturnType<typeof setTimeout> | null = null;
  const priorVisibility = new Map<THREE.Object3D, boolean>();

  const resolvedFullPixelRatio = () => Math.max(0.5, fullPixelRatio());
  let appliedPixelRatio = resolvedFullPixelRatio();
  const applyPixelRatio = (ratio: number) => {
    if (ratio === appliedPixelRatio) return;
    renderer.setPixelRatio(ratio);
    appliedPixelRatio = ratio;
  };
  const record = (state: "full" | "reduced", ratio: number, suppressed: number) => {
    Object.assign(host.dataset, {
      interactionHeavyOverlayCount: String(priorVisibility.size),
      interactionHeavyOverlaysSuppressed: String(suppressed),
      interactionPixelRatio: ratio.toFixed(2),
      interactionQuality: state,
    });
  };
  const suppressHeavyOverlays = (): number => {
    root.traverse((object) => {
      if (object.userData[LOOM_INTERACTION_HEAVY_OVERLAY] !== true) return;
      if (!priorVisibility.has(object)) priorVisibility.set(object, object.visible);
      object.visible = false;
    });
    return [...priorVisibility.values()].filter(Boolean).length;
  };
  const cancelRestore = () => {
    if (restoreTimer !== null) clearTimeout(restoreTimer);
    restoreTimer = null;
  };
  const restore = (render: boolean) => {
    cancelRestore();
    if (!reduced) return;
    priorVisibility.forEach((visible, object) => { object.visible = visible; });
    priorVisibility.clear();
    const ratio = resolvedFullPixelRatio();
    applyPixelRatio(ratio);
    reduced = false;
    record("full", ratio, 0);
    if (render) requestRender();
  };

  const initialRatio = appliedPixelRatio;
  record("full", initialRatio, 0);

  const reduce = () => {
    cancelRestore();
    const ratio = Math.min(resolvedFullPixelRatio(), reducedPixelRatio);
    const suppressed = suppressHeavyOverlays();
    if (!reduced) {
      applyPixelRatio(ratio);
      host.dataset.interactionReductionCount = String(
        Number(host.dataset.interactionReductionCount ?? 0) + 1,
      );
    }
    reduced = true;
    record("reduced", ratio, suppressed);
    requestRender();
  };
  const settle = () => {
    if (!reduced || interactionActive || restoreTimer !== null) return;
    restoreTimer = setTimeout(() => restore(true), settleDelayMs);
  };

  return {
    begin: () => {
      interactionActive = true;
      reduce();
    },
    end: () => {
      interactionActive = false;
      settle();
    },
    keepMoving: reduce,
    refreshOverlays: () => {
      if (!reduced) return;
      record("reduced", Math.min(resolvedFullPixelRatio(), reducedPixelRatio), suppressHeavyOverlays());
    },
    settle,
    isReduced: () => reduced,
    dispose: () => {
      interactionActive = false;
      restore(false);
    },
  };
}
